/*
  One game: its header, a language toggle, a filter, and its sets as an A-Z index (the card
  grid and table modes were dropped, pop-language-split D27). Originally: a card grid, a
  table or an A-Z index (§6.5, §7.2).

  This is the view that owns the filter + sort + pagination wiring for all three display
  modes,
  composed exactly as the plan's task-6b block specifies:

    - the filter lives INSIDE the table state, so setFilter is the only way to change it
      and it always resets page to 0. §6.5 recorded that rule and this view was one of the
      four that bypassed it with a useState of its own.
    - selectTableRows composes filter -> sort -> paginate once, so the grid and the table
      cannot end up with different ideas of what is showing.
    - pagination is given view.page (clamped) and view.filteredCount (post-filter), so a
      filter that shortens the results below the current page cannot paint a blank table.

  Three things that were wrong here and are fixed rather than carried:

    - **Load More is no longer hidden while a filter is active** (§6.5, §12.19). Sets load
      200 at a time; hiding it during filtering meant that on a game with more than 200
      sets, searching for a set on the second page answered "No results". The scope of the
      search is now stated instead, and the input points at that sentence.
    - **The subtitle is gone** (§7.2). It was `gameInfo.category` — the literal "TCG" or
      "Sports", or blank space for an uncategorised game. A public page does not label a
      game with its internal taxonomy value.
    - **There is an h1** (§3.5, §12.31). It is visually hidden: the visible title is the
      logo image, and adding a second, textual one would change a design nobody asked to
      change. The element is what the page outline and the computed-font check need.
*/

import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronDown, ChevronUp, LoaderCircle } from 'lucide-react';

import Breadcrumbs from 'components/Breadcrumbs';
/* Only the rule: this page's h1 is sr-only and its heading block already centres. */
import FilterSearch from 'components/FilterSearch';
import GameHeader, { BucketFigures } from 'components/GameHeader';
import GameSets from 'components/GameSets';
import { gameSetsColumns } from 'components/Tables/GameSetsTable';
import PopulationBreakdown from 'components/PopulationBreakdown';
import SetsAlphabetical from 'components/SetsAlphabetical';
import { useLanguageBucket } from 'components/useLanguageBucket';

import { isFilterActive, selectTableRows, useTableState } from 'components/DataTable';
import { FOCUS_RING } from 'components/styles';
import { FEATURED_LIMIT, formatCount, usablePartName } from 'lib';
import { mergeSetMatches } from 'lib/mergeSetMatches';
import { combineSetsByName } from 'lib/sets';
import { LangBucket, bucketOf, bucketsPresent } from 'lib/setLanguage';
import { displayNames } from 'lib/setName';

import { isRankedByCardsGraded, SETS_PAGE_SIZE } from 'lib/setPages';
import { useGetGamesQuery, useGetSetsQuery } from 'services/pcgPublicV2';
import { useAllGameSets } from 'services/useAllGameSets';
import { Set } from 'types/gameInfo';
import type { GameSetSummaryDto } from 'types/v2/catalog';

/* The sentence the filter input is described by: what the search can and cannot reach. */
const SCOPE_HINT_ID = 'game-sets-scope-hint';

const ARCHIVED_SETS_ID = 'game-archived-sets';

/* A v2 set row in the v1 shape the grid, table and index read. */
const toSet = (set: GameSetSummaryDto): Set => ({
  id: set.id,
  setName: set.name,
  cardsGraded: set.cardsGraded ?? 0,
  setImage: set.imageUrl,
  releaseDate: set.releaseDate,
  language: set.language ?? null,
  // Kept because the shared Set type requires it; no column reads it (D24).
  setCategory: null,
  // Absent on a backend without scope=population, which reads as not archived.
  archived: set.archived === true,
});

function GameView() {
  const params = useParams();
  const { gameCode = '' } = params;

  /* The Archived sets block starts collapsed: those sets are history, not the catalogue. */
  const [showArchived, setShowArchived] = useState(false);

  const {
    data: games = [],
    isLoading: isLoadingGames,
    error: gamesError,
  } = useGetGamesQuery();
  const gameInfo = games.find(g => g.gameCode.toLowerCase() === gameCode.toLowerCase());
  const gameId = gameInfo?.id;
  const game = gameInfo?.name || '';
  const found = !!gameInfo;

  /*
    D31: the whole catalogue, fetched up front rather than behind a "Load more" button,
    because an A-Z index over part of it is actively misleading — a letter reads as empty
    when it only has not been fetched yet. Page 0 first, then every later page at once
    (services/useAllGameSets). `sets` grows as pages land; `complete` is all of them.
    error is passed on: DataTable renders the error state.
  */
  const {
    sets: catalogue,
    complete: catalogComplete,
    total: catalogTotal,
    firstPage,
    error,
  } = useAllGameSets(gameId);

  const totalSets = catalogTotal ?? 0;
  const hasMore = !catalogComplete && !error;

  /* Mapped from the v2 set structure to the v1 shape the grid, table and index read. */
  const allSets = useMemo(() => catalogue.map(toSet), [catalogue]);

  /*
    The language bucket (docs/specs/pop-language-split.md D1-D7). The buttons are the
    buckets the LIVE sets fall in (D2); the cards per bucket are only for D5's last-resort
    pick. The bucket itself is derived, not stored (components/useLanguageBucket), so it
    settles by itself as pages arrive.

    Everything below the header reads `bucketSets` — the Top 10, all three list modes, the
    search and the archived block (D7) — so the bucket is applied once, here, before any
    combining. Display names are worked out over the bucket too, because D11's twins are
    twins within what one bucket shows.
  */
  const liveSets = useMemo(() => allSets.filter(set => !set.archived), [allSets]);
  const buckets = useMemo(() => bucketsPresent(liveSets), [liveSets]);
  const cardsByBucket = useMemo(
    () =>
      liveSets.reduce<Partial<Record<LangBucket, number>>>((sums, set) => {
        const key = bucketOf(set);
        sums[key] = (sums[key] ?? 0) + (set.cardsGraded ?? 0);
        return sums;
      }, {}),
    [liveSets]
  );
  const declaredBuckets = useMemo(
    () =>
      gameInfo?.languages
        ? /* Deduped by filter: `Set` in this file is the imported set type, not the built-in. */
          gameInfo.languages
            .map(language => bucketOf({ language, setName: '' }))
            .filter((value, index, all) => all.indexOf(value) === index)
        : undefined,
    [gameInfo]
  );
  const [bucket, setBucket] = useLanguageBucket(
    buckets,
    cardsByBucket,
    catalogComplete || !!error,
    declaredBuckets
  );
  const bucketSets = useMemo(
    () => allSets.filter(set => bucketOf(set) === bucket),
    [allSets, bucket]
  );
  const columns = useMemo(() => gameSetsColumns(gameCode), [gameCode]);
  /* 25 was the old reducer's page size (limit: 25); the default here is 10. */
  const table = useTableState({ orderBy: 'cardsGraded', order: 'desc', rowsPerPage: 25 });
  const { setFilter } = table;

  /*
    R14 of docs/specs/pop-report-followups.md. While pages are still arriving, the search
    also asks the server for sets whose name contains it (nameLike), so a set on a page
    that has not landed is still found. Once every page is in, the client filter has the
    whole catalogue and the request is skipped. `currentData`, not `data`, so the matches
    for the previous search text never stand in for this one's while it is in flight.

    The matches only widen what the SEARCH sees (`searchSets`): the Top 10, the counts and
    the bucket choice stay on the pages loaded, so a search cannot move them.
  */
  const partName = usablePartName(table.state.filter);
  const { currentData: serverMatches } = useGetSetsQuery(
    { gameId, nameLike: partName ?? undefined, offset: 0, limit: SETS_PAGE_SIZE },
    { skip: catalogComplete || !partName || gameId === undefined }
  );
  const searchSets = useMemo(
    () =>
      !catalogComplete && partName && serverMatches?.data.length
        ? mergeSetMatches(catalogue, serverMatches.data).map(toSet)
        : allSets,
    [catalogComplete, partName, serverMatches, catalogue, allSets]
  );
  const searchBucketSets = useMemo(
    () =>
      searchSets === allSets ? bucketSets : searchSets.filter(set => bucketOf(set) === bucket),
    [searchSets, allSets, bucketSets, bucket]
  );
  /*
    Display names twice over. `names` is over the loaded sets only and names the Top 10, the
    archived block and combinedSets, so a server match that cleans to a shelf set's name
    cannot rename that tile mid-typing (D11's twin fallback). `searchNames` is over the
    search's sets, R14's server matches included, so the index names those alike.
  */
  const names = useMemo(
    () => displayNames(bucketSets, set => bucket === 'other' && !(set.language ?? '').trim()),
    [bucketSets, bucket]
  );
  const searchNames = useMemo(
    () =>
      searchBucketSets === bucketSets
        ? names
        : displayNames(searchBucketSets, set => bucket === 'other' && !(set.language ?? '').trim()),
    [searchBucketSets, bucketSets, names, bucket]
  );
  /*
    D30. Same-named rows are usually two language editions of one set, and both link to the
    same page, whose population is the two added together. Combined here, above the table
    state, so the card grid and the table view cannot disagree — and above the filter, so
    "N matching" counts what is on screen.

    `allSets` stays raw on purpose: the "N of M sets loaded" hint below reports how much of
    the catalog has been fetched, which is a count of API rows, not of cards.

    Archived sets (scope=population lists those with gradings) are split off before
    combining, so they never reach the ranked shelf, the table or the index and are shown
    in their own collapsed block instead.

    Both lists are the bucket's only, and each row carries its display name (D10, D11).
  */
  const combinedSets = useMemo(
    () =>
      combineSetsByName(bucketSets.filter(set => !set.archived)).map(set => ({
        ...set,
        displayName: names.get(set.setName.trim()) ?? set.setName,
      })),
    [bucketSets, names]
  );
  /* What the search and the A-Z index read: combinedSets plus R14's server matches. */
  const searchCombinedSets = useMemo(
    () =>
      searchBucketSets === bucketSets
        ? combinedSets
        : combineSetsByName(searchBucketSets.filter(set => !set.archived)).map(set => ({
            ...set,
            displayName: searchNames.get(set.setName.trim()) ?? set.setName,
          })),
    [searchBucketSets, bucketSets, combinedSets, searchNames]
  );
  const archivedSets = useMemo(
    () =>
      combineSetsByName(bucketSets.filter(set => set.archived))
        .map(set => ({ ...set, displayName: names.get(set.setName.trim()) ?? set.setName }))
        .sort((a, b) => (b.cardsGraded ?? 0) - (a.cardsGraded ?? 0)),
    [bucketSets, names]
  );

  /*
    The most graded sets of this game, at the same count the home page features games
    (D33). Taken off the COMBINED list, so two language editions of one set are one card
    here exactly as they are below, and their counts are already summed — ranking the raw
    rows would let a set appear twice and rank each half on its own total.
  */
  /*
    When the shelf may rank. A ranking over an arbitrary part of the catalogue is not a
    partial answer, it is a wrong one that reads as authoritative — that is how a shelf
    headed "Top 8 Graded Sets" once showed the top of page 2. It used to wait for the last
    page for that reason, which on Pokémon meant 1,022 sets fetched one page at a time
    before the first tile, about 5.5 seconds of skeletons.

    Page 0 is not an arbitrary part. The API returns sets most graded first, so page 0 IS
    the top 200 rows, and the top 10 of the combined list is in it. The one way a set
    outside it could still change the shelf is D30's name-combining: a second language
    edition beyond row 200 adding its count to a set. Each such row adds no more than row
    200's own count, which is small beside a top-ten set's; on 2026-10-20 the page-0 Top 10
    matched the full-catalogue Top 10 for all 214 games. And the shelf re-ranks off everything in hand as later pages land, so if one
    ever did, it would correct itself within a second rather than stay wrong.

    The order is checked on the payload (isRankedByCardsGraded), not trusted: if the API
    ever stopped sorting, the shelf would go back to waiting for the last page. A page 0
    with fewer than ten live sets on it also waits, since the rest may be further on.

    "Ten live sets" means ten in the BUCKET being shown (pop-language-split D7): page 0 is
    the top 200 of the whole game, so it holds the top 10 of English or Japanese, but
    Chinese and Other rarely have ten sets in it and those shelves wait for the last page.
  */
  const firstPageRanks = useMemo(
    () =>
      !!firstPage &&
      isRankedByCardsGraded(firstPage) &&
      combineSetsByName(
        firstPage
          .filter(set => !set.archived)
          .map(toSet)
          .filter(set => bucketOf(set) === bucket)
      ).length >= FEATURED_LIMIT,
    [firstPage, bucket]
  );
  const rankingReady = catalogComplete || firstPageRanks;

  const topSets = useMemo(
    () =>
      [...combinedSets]
        .sort((a, b) => (b.cardsGraded ?? 0) - (a.cardsGraded ?? 0))
        .slice(0, FEATURED_LIMIT),
    [combinedSets]
  );

  const view = useMemo(
    () => selectTableRows(searchCombinedSets, columns, table.state),
    [searchCombinedSets, columns, table.state]
  );

  // Reset on game change. FilterSearch holds the text a visitor is mid-way through
  // typing, so it is keyed on gameCode below to clear with the rest of it. The catalogue
  // needs no reset: useAllGameSets files pages by game.
  useEffect(() => {
    setFilter('');
    setShowArchived(false);
  }, [gameCode, setFilter]);

  if (!found) {
    /*
      "Does not exist" is a claim about the catalog, so it needs a catalog to have been
      read. Two things have to be ruled out before it is true:

        - the list still being in flight, which used to tell every visitor their game was
          gone for as long as the request took, and
        - the list having FAILED. gamesError was discarded, so a blocked or offline
          /v2/catalog/games turned every game URL into "does not exist" — a wrong answer
          stated with full confidence, where §6.2 asks the view to surface the error.
    */
    if (isLoadingGames) {
      return (
        /* Holds a screen of height, so the footer is not pulled into view and then shoved off it. */
        <p role="status" className="min-h-screen py-16 text-center text-pcg-textMuted">
          Loading game…
        </p>
      );
    }

    if (gamesError) {
      return (
        <p role="alert" className="py-16 text-center text-pcg-text">
          Games could not be loaded. Please try again.
        </p>
      );
    }

    return (
      <p className="py-16 text-center text-pcg-text">
        Game does not exist or is not supported.
      </p>
    );
  }

  /*
    A skeleton until the first page lands — or, for a bucket none of whose sets has arrived
    yet (a wished-for Japanese while page 0 is all English, say), until one does or the
    catalogue is complete. An empty bucket mid-walk is "not here yet", not "no results".
  */
  const isInitialLoad =
    !error && (!firstPage || (!catalogComplete && bucketSets.length === 0));

  /*
    The counts are of LIVE sets. The API total includes the archived sets that
    scope=population lists, and those are shown in their own block below, so they come
    off both figures. While pages are still arriving only the archived rows loaded so far
    can be taken off, so the total can run a little high until the last page lands — the
    same "settles on the total" behaviour the count already had.
  */
  const archivedLoaded = allSets.filter(set => set.archived).length;
  const loadedCount = formatCount(allSets.length - archivedLoaded);
  /*
    Once the walk is done the total is the bucket's own: its live sets as listed, same-named
    rows combined, so "N sets" is the number of entries on screen (pop-language-split D7).
    Until then it is the catalogue-wide figure above, which is what the progress line needs.
  */
  const total = catalogComplete
    ? formatCount(combinedSets.length)
    : formatCount(Math.max(0, totalSets - archivedLoaded));
  /*
    The header's Cards graded and Sets graded, over the same live bucket sets: "…" until the
    walk is done (D8), and left out if it failed, since a failed walk never completes.
  */
  const bucketFigures: BucketFigures | 'pending' | null = error
    ? null
    : catalogComplete
      ? {
          cardsGraded: combinedSets.reduce((sum, set) => sum + (set.cardsGraded ?? 0), 0),
          sets: combinedSets.length,
        }
      : 'pending';
  /* D12: only Other mixes languages, so only Other keeps the language chips. */
  const showLanguage = bucket === 'other';
  /*
    While pages are still arriving the hint says how far through the catalogue the page
    is. It used to add "The search matches loaded sets only.", which R14 made untrue: the
    search now asks the server for the sets that have not loaded. The matching count is no
    longer "in the N loaded" for the same reason.
  */
  const scopeHint = hasMore
    ? isFilterActive(table.state.filter)
      ? `${formatCount(view.filteredCount)} matching. ${loadedCount} of ${total} sets loaded so far.`
      : `${loadedCount} of ${total} sets loaded so far.`
    : isFilterActive(table.state.filter)
      ? `${formatCount(view.filteredCount)} of ${total} sets match.`
      : `${total} sets.`;

  return (
    <>
      <Helmet title={game} />
      <div className="skin-stack flex flex-col gap-6">
        <Breadcrumbs
          crumbs={[
            {
              url: '/',
              text: 'Home',
            },
            {
              url: '',
              text: game,
            }
          ]}
        />

        {/*
          The game's header card: logo, name and figures (components/GameHeader). It used to
          be the logo alone, centred over a rule, with the h1 sr-only.
        */}
        {gameInfo ? (
          <GameHeader
            game={gameInfo}
            games={games}
            figures={bucketFigures}
            buckets={buckets}
            bucket={bucket}
            onBucketChange={setBucket}
          />
        ) : null}

        {/*
          The most graded sets, above the searchable list — the shape the home page and the
          admin catalog both use: a short ranked shelf first, then everything.

          Gone entirely on error, where the table below is the one that reports it, and
          gone once the walk has finished with nothing to rank.
        */}
        {!error && (!rankingReady || topSets.length > 0) ? (
          <section aria-labelledby="top-sets-heading">
            <div className="mb-4 flex items-center gap-3">
              <h2
                id="top-sets-heading"
                className="font-display text-lg font-semibold text-pcg-text sm:text-xl"
              >
                Top {FEATURED_LIMIT} Graded Sets
              </h2>
              <span aria-hidden="true" className="h-px flex-1 bg-pcg-goldMuted" />
              <span className="shrink-0 text-xs uppercase tracking-wide text-pcg-textMuted">
                Ranked by cards graded
              </span>
            </div>
            <GameSets
              data={topSets}
              gameCode={gameCode}
              isLoading={!rankingReady}
              error={undefined}
              showLanguage={showLanguage}
              eager
            />
          </section>
        ) : null}

        {/*
          Heading, hairline, count, and the search on the same row (Robert, 2026-10-20:
          "there is a lot of dead space here. I don't think they need the options to change
          the way it presents" — pop-language-split D27). The card / table / A-Z switch is
          gone: All Sets is the A-Z index only, and the Top 10 shelf above already shows
          the sets as tiles. The count is the search's own description, so it is the honest
          one: it falls while pages arrive, settles on the bucket's total, and says how
          many match while a search is typed. It wraps under the heading on a phone.
        */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <h2 className="font-display text-lg font-semibold text-pcg-text sm:text-xl">
            All Sets
          </h2>
          <span aria-hidden="true" className="hidden h-px min-w-[2rem] flex-1 bg-pcg-goldMuted sm:block" />
          <span
            id={SCOPE_HINT_ID}
            className="ml-auto shrink-0 text-sm text-pcg-textMuted sm:ml-0"
          >
            {scopeHint}
          </span>
          <FilterSearch
            key={gameCode}
            onChange={setFilter}
            label="Search Sets"
            hideLabel
            placeholder="Search sets"
            describedBy={SCOPE_HINT_ID}
            className="w-full sm:w-72"
          />
        </div>

        {/*
          Scoped to this game (§9.2). gameInfo is resolved above and `found` has already
          returned early without it, so the id is always present by here.
        */}
        <PopulationBreakdown scope="game" scopeId={gameInfo?.id} />

        {/*
          The index is given the filtered rows, not `combinedSets`: §12.19 asks that the
          search stay usable, and a letter list that ignored it would do nothing on typing.
        */}
        <SetsAlphabetical
          data={view.filteredRows}
          gameCode={gameCode}
          isLoading={isInitialLoad}
          error={error}
          showLanguage={showLanguage}
        />

        {hasMore ? (
          /*
            A progress line, not a button. Sets keep arriving on their own, and the count
            says how far through the catalog the page is so a long game does not look stuck.
          */
          <p className="flex items-center justify-center gap-2 text-sm text-pcg-textMuted" role="status">
            <LoaderCircle aria-hidden="true" className="h-4 w-4 animate-spin" />
            Loading sets… {loadedCount} of {total}
          </p>
        ) : null}

        {/*
          Archived sets that still have gradings, collapsed until asked for. Tiles only,
          unfiltered, ranked by cards graded: a handful of rows needs no modes or search.
        */}
        {!error && archivedSets.length > 0 ? (
          <section aria-labelledby="archived-sets-heading">
            <div className="mb-4 flex items-center gap-3">
              <h2
                id="archived-sets-heading"
                className="font-display text-lg font-semibold text-pcg-text sm:text-xl"
              >
                <button
                  type="button"
                  aria-expanded={showArchived}
                  aria-controls={ARCHIVED_SETS_ID}
                  onClick={() => setShowArchived(current => !current)}
                  className={`inline-flex items-center gap-2 rounded-md hover:text-pcg-goldText ${FOCUS_RING}`}
                >
                  Archived sets
                  {showArchived ? (
                    <ChevronUp aria-hidden="true" className="h-5 w-5" />
                  ) : (
                    <ChevronDown aria-hidden="true" className="h-5 w-5" />
                  )}
                </button>
              </h2>
              <span aria-hidden="true" className="h-px flex-1 bg-pcg-goldMuted" />
              <span className="shrink-0 text-xs uppercase tracking-wide text-pcg-textMuted">
                {formatCount(archivedSets.length)} sets
              </span>
            </div>
            <div id={ARCHIVED_SETS_ID} hidden={!showArchived}>
              {showArchived ? (
                <GameSets
                  data={archivedSets}
                  gameCode={gameCode}
                  error={undefined}
                  showLanguage={showLanguage}
                />
              ) : null}
            </div>
          </section>
        ) : null}
      </div>
    </>
  );
}

export default GameView;
